import React, { useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, MessagesSquare, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { commentColumns, toCommentRow } from "./tableParams/commentColumns";
import CommentAPI from "../../api/CommentAPI";
import useAbility from "../../hooks/useAbility";
import CappedPage from "../common/CappedPage";
import DataTable from "../dataTable/DataTable";
import DataTableEmptyState from "../dataTable/DataTableEmptyState";
import { commentViewPath } from "../routes/routes";
import Breadcrumbs from "../utils/Breadcrumbs";
import InfoPanel from "../utils/InfoPanel";

import type { JSX } from "react";

import type { CommentRow } from "./comment.types";
import type { AppState } from "../../reducers";
import type { BulkAction, RowAction } from "../dataTable/dataTable.types";

const INFO_PANEL_LINKS = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000097714-activate-comments-on-your-platform",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097714-activez-les-commentaires-sur-votre-plateforme",
};

const EMPTY_ROW_LIST: CommentRow[] = [];

function Comments(): JSX.Element {
  const { t } = useTranslation("editor-comments");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const queryClient = useQueryClient();
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const { canAccess } = useAbility();
  const { project: canAccessProject } = canAccess(orgaOptions);

  const getCommentListQuery = CommentAPI.generateQuery({
    fn: CommentAPI.getCommentList,
    params: [{ projectSlug }],
  });

  const { data, isLoading, isError, refetch } = useQuery(getCommentListQuery);

  const rowList = useMemo(
    () => data?.map(toCommentRow) ?? EMPTY_ROW_LIST,
    [data],
  );

  const handleReload = (): void => {
    queryClient.invalidateQueries({ queryKey: getCommentListQuery.queryKey });
  };

  const { mutate: validateComments } = useMutation({
    mutationFn: async (uuids: string[]) =>
      CommentAPI.validateComments({ projectSlug, uuids }),
    onSuccess: (_, uuids) => {
      toast.success(
        t(`editor-notif:validateSuccess${uuids.length > 1 ? "_plural" : ""}`),
      );
      handleReload();
    },
    onError: () => {
      toast.error(t("editor-notif:validateError"));
    },
  });

  const { mutate: deleteComments } = useMutation({
    mutationFn: async (uuids: string[]) =>
      CommentAPI.deleteComments({ projectSlug, uuids }),
    onSuccess: (_, uuids) => {
      toast.success(
        t(`editor-notif:deleteSuccess${uuids.length > 1 ? "_plural" : ""}`),
      );
      handleReload();
    },
    onError: () => {
      toast.error(t("editor-notif:deleteError"));
    },
  });

  // Mêmes libellés et confirmations pour une ligne et pour la sélection.
  const { columnList, bulkActionList } = useMemo(() => {
    const validateAction: Omit<RowAction, "onClick"> = {
      label: t("tooltip:verified"),
      icon: <BadgeCheck />,
      confirm: {
        title: t("popup:confirm"),
        description: t("popup:confirm_verified"),
        confirmLabel: t("popup:submit"),
      },
    };
    const deleteAction: Omit<RowAction, "onClick"> = {
      label: t("tooltip:delete"),
      icon: <Trash2 />,
      variant: "destructive",
      confirm: {
        title: t("popup:confirm"),
        description: t("popup:confirm_delete"),
        confirmLabel: t("popup:submit"),
      },
    };
    const commentBulkActionList: BulkAction<CommentRow>[] = [
      {
        ...validateAction,
        onClick: (comments) =>
          validateComments(comments.map((comment) => comment.uuid)),
      },
      {
        ...deleteAction,
        onClick: (comments) =>
          deleteComments(comments.map((comment) => comment.uuid)),
      },
    ];

    return {
      bulkActionList: commentBulkActionList,
      columnList: commentColumns({
        t,
        projectSlug,
        canAccessUsers: canAccessProject.usersAnalytics,
        getRowActions: (comment) => [
          {
            ...validateAction,
            hidden: comment.valid,
            onClick: () => validateComments([comment.uuid]),
          },
          { ...deleteAction, onClick: () => deleteComments([comment.uuid]) },
        ],
      }),
    };
  }, [
    t,
    projectSlug,
    canAccessProject.usersAnalytics,
    validateComments,
    deleteComments,
  ]);

  const emptyState = (
    <React.Fragment>
      <DataTableEmptyState
        icon={MessagesSquare}
        message={t("noComments")}
        title={t("sectionTitle")}
      />
      <InfoPanel links={INFO_PANEL_LINKS} />
    </React.Fragment>
  );

  return (
    <CappedPage contentClassName="pt-7.5 pb-15">
      <Breadcrumbs items={[{ name: t("editor-menu:comments") }]} />
      <DataTable
        bulkActions={bulkActionList}
        columns={columnList}
        data={rowList}
        defaultSorting={{ id: "inserted_at", desc: true }}
        description={<InfoPanel links={INFO_PANEL_LINKS} />}
        emptyState={emptyState}
        getRowHref={(comment) => commentViewPath(projectSlug, comment.uuid)}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={t("sectionTitle")}
      />
    </CappedPage>
  );
}

export default Comments;
